import React, { Suspense, lazy, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { PrimaryButton, SecondaryButton, TertiaryButton } from 'src/shared/components/button/Button';
import { asText } from 'src/shared/components/json-editor/documentText';

/**
 * Loaded when someone opens JSON mode, not when the app starts.
 *
 * CodeMirror is a few hundred kilobytes, the catalogue route is imported eagerly, and this
 * editor is reachable only by a super user who has chosen to edit a raw document — so left as a
 * plain import it is shipped to every reader of the dashboard for a screen almost none of them
 * can open.
 */
const JsonEditor = lazy(() => import('src/shared/components/json-editor/JsonEditor'));

type DocumentEditorProps = {
    /** What is being edited, for the wording: `version` or `dataset`. */
    noun: string;
    /** What the form holds right now, as a document. Seeds the editor and is what Copy takes. */
    document: unknown;
    /** Hands the edited document back to the form. Not a save. */
    onApply: (document: Record<string, unknown>) => void;
    /** Closes the hatch, leaving the form as it was. */
    onCancel: () => void;
};

/**
 * The form flipped open: the whole document, as JSON.
 *
 * The escape hatch for everything the form has no input for — and, more usefully, the way to
 * lift a version or a dataset out of one catalogue and drop it into another rather than
 * retyping it field by field. One editor for both, because the two are the same act.
 *
 * It applies back into the form rather than saving: the form states the engine's rules and owns
 * the Save, so a document that came through here is checked exactly like one that was typed.
 */
const DocumentEditor = ({ noun, document, onApply, onCancel }: DocumentEditorProps) => {
    const [text, setText] = useState(() => asText(document));
    const [unreadable, setUnreadable] = useState<string | undefined>();
    const [isCopied, setIsCopied] = useState(false);

    const copy = async () => {
        await navigator.clipboard?.writeText(text);
        setIsCopied(true);
        window.setTimeout(() => setIsCopied(false), 1500);
    };

    const apply = (event: React.FormEvent) => {
        event.preventDefault();

        let parsed: unknown;
        try {
            parsed = JSON.parse(text);
        } catch (error) {
            setUnreadable(`This is not readable JSON: ${(error as Error).message}`);
            return;
        }

        // A STAC document is an object. An array or a scalar would be refused by the engine
        // with a message about schemas rather than about what was pasted.
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
            setUnreadable(`A ${noun} is a JSON object. This is ${Array.isArray(parsed) ? 'an array' : typeof parsed}.`);
            return;
        }

        setUnreadable(undefined);
        onApply(parsed as Record<string, unknown>);
    };

    return (
        <form className='flex w-full max-w-3xl flex-col gap-4' onSubmit={apply} data-testid='document-editor' noValidate>
            <section className='flex flex-col gap-4 rounded-xl border border-border bg-white p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)]'>
                <h3 className='relative flex items-center justify-between pb-4 text-lg font-light tracking-tight text-foreground after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-gradient-to-r after:from-primary after:to-primary/10'>
                    Editing the raw {noun}
                </h3>

                <div className='flex flex-wrap items-center justify-between gap-2'>
                    <p className='text-xs text-muted-foreground'>
                        The whole {noun} as the catalogue holds it. Paste one from another catalogue to copy it here — applying brings it
                        back into the form, where it is saved like any other edit.
                    </p>
                    <TertiaryButton type='button' onClick={copy} data-testid='document-copy'>
                        {isCopied ? <Check className='mr-1 h-3.5 w-3.5' /> : <Copy className='mr-1 h-3.5 w-3.5' />}
                        {isCopied ? 'Copied' : 'Copy'}
                    </TertiaryButton>
                </div>

                <Suspense
                    fallback={
                        <p
                            className='rounded border border-border px-3 py-2 text-xs text-muted-foreground'
                            data-testid='document-json-loading'
                        >
                            Opening the editor&hellip;
                        </p>
                    }
                >
                    <JsonEditor
                        value={text}
                        onChange={next => {
                            setText(next);
                            // Cleared on edit rather than reparsed: half-typed JSON is not yet wrong,
                            // and the editor marks a real break as it happens without being asked.
                            setUnreadable(undefined);
                        }}
                    />
                </Suspense>

                {unreadable && (
                    <p className='text-sm text-red-100' data-testid='document-json-error' role='alert'>
                        {unreadable}
                    </p>
                )}
            </section>

            <div className='flex gap-3'>
                <PrimaryButton type='submit' data-testid='document-json-apply'>
                    Apply to form
                </PrimaryButton>
                <SecondaryButton type='button' onClick={onCancel} data-testid='document-json-cancel'>
                    Cancel
                </SecondaryButton>
            </div>
        </form>
    );
};

export default DocumentEditor;
